/**
 * SOUND-827 — pure logic for CheckoutChamp routing CHANGE SETS (Phase 2):
 * propose → tick → GO → apply → verify → roll back.
 *
 * This phase WRITES TO LIVE CHECKOUTCHAMP, and there is no sandbox. sound-api is
 * the real enforcement (verified-ops list, per-env write allowlist, staleness
 * guard, sum-to-100, admin-only approval). Everything here exists so the UI never
 * offers something the API is going to refuse, and so that what the approver
 * sees — labels, resulting totals, side effects — matches what CC will do.
 *
 * Builds on utilities/routingView (Phase 1): ids through `itemId`, flags through
 * `toFlag`, numbers through `toNumber`, MID labels through `resolveMid`, totals
 * through `percentageSummary`. CC values are stored VERBATIM (mixed types), so
 * comparisons go through `sameValue`, which is the contract's rule 4 verbatim.
 *
 * Verified CC write behaviour this file encodes (Primal Storm profile 3):
 *   - CC does NOT enforce that WEIGHTED percentages sum to 100. Sound must.
 *   - Setting a profile MID `enabled: 0` silently sets its `percentage` to 0,
 *     and re-enabling does NOT restore it.
 *   - Algorithm enum: WEIGHTED | ROUND_ROBIN | PRIORITY | INTELLIGENT.
 *
 * Lifted out of the pages because sound-app has no component-test harness
 * (SOUND-679); this is the only level at which the section is testable.
 */
import { EMPTY_DISPLAY, displayValue } from "utilities/displayValue";
import { isInternal } from "utilities/userType";
import {
  itemId,
  toFlag,
  toNumber,
  formatFlag,
  formatPercent,
  resolveMid,
  isGatewayEnabled,
  objectTypeLabel,
} from "utilities/routingView";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** CC's algorithm enum, INTELLIGENT included (undocumented, verified). */
export const ROUTING_ALGORITHMS = ["WEIGHTED", "ROUND_ROBIN", "PRIORITY", "INTELLIGENT"];

/** MID-row limit type, per the CC docs and live data. */
export const GATEWAY_LIMIT_TYPES = ["DAILY", "MONTHLY"];

/** The MID-row fields the Profiles editor exposes (contract, Frontend §1). */
export const PROFILE_GATEWAY_FIELDS = [
  "percentage",
  "priority",
  "enabled",
  "limitType",
  "salesLimit",
  "profileCap",
];

/** `gateway.add` takes these optional params (verified call) — no `enabled`. */
export const GATEWAY_ADD_FIELDS = ["percentage", "priority", "limitType", "salesLimit", "profileCap"];

/** Rule 13 — at most 50 ticked changes per GO (Heroku's 30s limit). */
export const MAX_CHANGES_PER_GO = 50;

/** Rule 5's tolerance. */
const SUM_TOLERANCE = 0.01;

// Fields that are never editable (contract, Routes §POST change-sets), plus the
// object's own id and the gateway integration type (`gatewayId`), which is an id.
const NEVER_EDITABLE = new Set([
  "id",
  "gatewayId",
  "lastUpdated",
  "dateCreated",
  "dateUpdated",
  "gatewayName",
  "zoneName",
  "merchantCategoryName",
  "campaignName",
  "gatewayData",
  "slot1",
  "slot2",
]);

const OBJECT_ID_FIELD = {
  gateways: "billerId",
  profiles: "id",
  cascades: "cascadeId",
  binMaps: "id",
  binProfiles: "binProfileId",
  recycle: "id",
};

// Numeric fields: rule 4's "blank equals 0" applies to the cap/limit ones only.
const BLANK_IS_ZERO = new Set(["profileCap", "salesLimit", "monthlyLimit"]);
const INTEGER_FIELDS = new Set(["priority", "salesLimit", "profileCap", "maxDeclines", "maxHardDeclines"]);
const DECIMAL_FIELDS = new Set(["percentage"]);

const FIELD_LABELS = {
  percentage: "percentage",
  priority: "priority",
  enabled: "enabled",
  limitType: "limit type",
  salesLimit: "sales limit",
  profileCap: "profile cap",
  algorithm: "algorithm",
  calcInterval: "calc interval",
  maxDeclines: "max declines",
  maxHardDeclines: "max hard declines",
  cascadeId: "cascade",
  title: "title",
};

export function fieldLabel(field) {
  return FIELD_LABELS[field] || String(field || "");
}

// ---------------------------------------------------------------------------
// sameValue — the contract's rule 4, used for staleness, validation and verify
// ---------------------------------------------------------------------------

function isBlank(v) {
  return v === null || v === undefined || (typeof v === "string" && v.trim() === "");
}

/**
 * - null, undefined and "" are all blank and equal each other;
 * - for profileCap / salesLimit / monthlyLimit, blank also equals 0 / "0";
 * - two values that both parse as finite numbers compare numerically;
 * - otherwise String(a) === String(b).
 */
export function sameValue(a, b, field) {
  const blankA = isBlank(a);
  const blankB = isBlank(b);
  if (blankA && blankB) return true;
  if (field && BLANK_IS_ZERO.has(field) && (blankA || blankB)) {
    const other = blankA ? b : a;
    return toNumber(other) === 0;
  }
  if (blankA || blankB) return false;
  const na = toNumber(a);
  const nb = toNumber(b);
  if (na !== null && nb !== null) return na === nb;
  return String(a) === String(b);
}

// ---------------------------------------------------------------------------
// Admin gating (the API is the real enforcement)
// ---------------------------------------------------------------------------

/**
 * Approver = internal admin: userType internal AND role admin | super
 * (contract rule 3). `useAuth().user` merges the /users/me profile, which
 * carries both `userType` and `role`.
 */
export function canApproveRouting(user) {
  if (!user || !isInternal(user)) return false;
  return user.role === "admin" || user.role === "super";
}

// ---------------------------------------------------------------------------
// Snapshot lookup
// ---------------------------------------------------------------------------

function itemsOf(snapshot, objectType) {
  const obj = snapshot?.objects?.[objectType];
  return obj && obj.access !== false && Array.isArray(obj.items) ? obj.items : [];
}

export function findItem(snapshot, objectType, ccId) {
  if (ccId === null || ccId === undefined) return null;
  const key = String(ccId);
  return itemsOf(snapshot, objectType).find((i) => itemId(objectType, i) === key) || null;
}

export function findGatewayRow(item, billerId) {
  if (!item || billerId === null || billerId === undefined) return null;
  const key = String(billerId).trim();
  return (Array.isArray(item.gateways) ? item.gateways : []).find(
    (g) => String(g?.billerId).trim() === key
  ) || null;
}

/** Human name for an object: its title / name, else "Profiles #3". */
export function objectTitle(objectType, item, ccId) {
  const t = item && (item.title || item.name);
  if (t && String(t).trim()) return String(t).trim();
  const id = item ? itemId(objectType, item) : ccId;
  return `${objectTypeLabel(objectType)} #${id ?? "?"}`;
}

/**
 * Fields the generic editor may offer for an item: scalar values only, minus the
 * never-editable list, the object's own id field, and `_sound`. A MID row also
 * loses `billerId` and `title` (contract).
 */
export function editableFields(objectType, item, { gatewayRow = false } = {}) {
  if (!item || typeof item !== "object") return [];
  const idField = gatewayRow ? "billerId" : OBJECT_ID_FIELD[objectType];
  return Object.keys(item).filter((k) => {
    if (k.startsWith("_")) return false;
    if (NEVER_EDITABLE.has(k) || k === idField) return false;
    if (gatewayRow && (k === "title" || k === "billerId")) return false;
    const v = item[k];
    return v === null || ["string", "number", "boolean"].includes(typeof v);
  });
}

// ---------------------------------------------------------------------------
// Draft building
//
// The draft is an ARRAY of changes in the order the user made them (that is the
// order shown to the approver, and the order CC is called in). One change per
// field: editing the same field again replaces it; editing it back to the live
// value drops it.
// ---------------------------------------------------------------------------

export function changeKey(c) {
  const op = c.op === "gateway.update" || c.op === "update" ? "field" : c.op;
  return [c.objectType, op, c.ccId ?? "", c.billerId ?? "", c.op === "gateway.add" || c.op === "gateway.remove" ? "" : c.field ?? ""]
    .map(String)
    .join("|");
}

function withKey(c) {
  return { ...c, key: changeKey(c) };
}

function rowKey(c) {
  return `${c.objectType}|${c.ccId ?? ""}|${c.billerId ?? ""}`;
}

/** The pending add for a MID row, if the draft has one. */
export function findAdd(draft, { objectType, ccId, billerId }) {
  return (draft || []).find(
    (c) =>
      c.op === "gateway.add" &&
      c.objectType === objectType &&
      String(c.ccId) === String(ccId) &&
      String(c.billerId) === String(billerId)
  ) || null;
}

export function findRemove(draft, { objectType, ccId, billerId }) {
  return (draft || []).find(
    (c) =>
      c.op === "gateway.remove" &&
      c.objectType === objectType &&
      String(c.ccId) === String(ccId) &&
      String(c.billerId) === String(billerId)
  ) || null;
}

/** The draft's change for one field (object-level or MID-row), or null. */
export function findFieldChange(draft, { objectType, ccId, billerId = null, field }) {
  const key = changeKey({ objectType, op: billerId != null ? "gateway.update" : "update", ccId, billerId, field });
  return (draft || []).find((c) => c.key === key) || null;
}

/**
 * Record an edit of one scalar field.
 *
 * `from` is the LIVE (snapshot) value and is kept from the first edit — later
 * edits of the same field only move `to`. Setting it back to `from` drops it.
 *
 * Two CC behaviours are folded in:
 *   - a field edit on a MID row the draft is ADDING edits the add's `to`, since
 *     the row does not exist in CC yet (a gateway.update would be stale);
 *   - disabling a MID row (enabled → 0) drops any percentage change on that row,
 *     because CC zeroes the share on disable and a later percentage write would
 *     fight it (verification expects 0).
 */
export function setFieldEdit(draft, { objectType, ccId, billerId = null, field, from, to }) {
  const list = Array.isArray(draft) ? draft : [];
  const isRow = billerId !== null && billerId !== undefined && billerId !== "";

  if (isRow) {
    const add = findAdd(list, { objectType, ccId, billerId });
    if (add) {
      return list.map((c) => (c === add ? { ...c, to: { ...(c.to || {}), [field]: to } } : c));
    }
  }

  const change = withKey({
    objectType,
    op: isRow ? "gateway.update" : "update",
    ccId: String(ccId),
    billerId: isRow ? String(billerId) : null,
    field,
    from,
    to,
  });
  const existing = list.find((c) => c.key === change.key);
  const liveFrom = existing ? existing.from : from;

  let next;
  if (sameValue(liveFrom, to, field)) {
    next = list.filter((c) => c.key !== change.key);
  } else if (existing) {
    next = list.map((c) => (c.key === change.key ? { ...c, to } : c));
  } else {
    next = [...list, { ...change, from: liveFrom }];
  }

  if (isRow && field === "enabled" && toFlag(to) === false) {
    next = next.filter(
      (c) => !(c.op === "gateway.update" && rowKey(c) === rowKey(change) && c.field === "percentage")
    );
  }
  return next;
}

/**
 * Add a MID to a profile/cascade. `values` are the optional add params
 * (percentage, priority, limitType, salesLimit, profileCap). Undoes a pending
 * remove of the same MID instead, if there is one.
 */
export function addGatewayEdit(draft, { objectType, ccId, billerId, values = {} }) {
  const list = Array.isArray(draft) ? draft : [];
  const removal = findRemove(list, { objectType, ccId, billerId });
  if (removal) return list.filter((c) => c !== removal);
  if (findAdd(list, { objectType, ccId, billerId })) return list;
  const to = {};
  for (const f of GATEWAY_ADD_FIELDS) {
    if (values[f] !== undefined) to[f] = values[f];
  }
  return [
    ...list,
    withKey({ objectType, op: "gateway.add", ccId: String(ccId), billerId: String(billerId), field: null, from: null, to }),
  ];
}

/**
 * Remove a MID from a profile/cascade. `row` is the live row; its scalar values
 * become `from` (the API's rollback re-adds with them). Any field edits on that
 * row are dropped — they would be meaningless. Removing a MID the draft is
 * adding just drops the add.
 */
export function removeGatewayEdit(draft, { objectType, ccId, billerId, row }) {
  const list = Array.isArray(draft) ? draft : [];
  const add = findAdd(list, { objectType, ccId, billerId });
  if (add) return list.filter((c) => c !== add);
  const rk = `${objectType}|${ccId}|${billerId}`;
  const without = list.filter((c) => !(c.op === "gateway.update" && rowKey(c) === rk));
  if (findRemove(without, { objectType, ccId, billerId })) return without;
  const from = {};
  for (const [k, v] of Object.entries(row || {})) {
    if (k === "title" || k.startsWith("_")) continue;
    if (v === null || ["string", "number", "boolean"].includes(typeof v)) from[k] = v;
  }
  return [
    ...without,
    withKey({ objectType, op: "gateway.remove", ccId: String(ccId), billerId: String(billerId), field: null, from, to: null }),
  ];
}

/** Undo one change. */
export function removeChange(draft, key) {
  return (Array.isArray(draft) ? draft : []).filter((c) => c.key !== key);
}

/**
 * Coerce a typed value to the type of the live value: a numeric string becomes a
 * number when CC sent a number there, stays a string when CC sent a string.
 * Blank stays "" (validation flags it).
 */
export function coerceInput(from, raw) {
  const s = raw === null || raw === undefined ? "" : String(raw).trim();
  if (s === "") return "";
  if (typeof from === "number" && toNumber(s) !== null) return Number(s);
  return s;
}

/** An enabled flag in the live value's own type: "1"/"0" stays a string. */
export function flagValue(from, on) {
  return typeof from === "string" ? (on ? "1" : "0") : on ? 1 : 0;
}

/** Why a change's `to` is not sendable, or null. */
export function validateChange(c) {
  if (!c) return null;
  if (c.op === "gateway.remove") return null;
  if (c.op === "gateway.add") {
    for (const [f, v] of Object.entries(c.to || {})) {
      const err = validateValue(f, v);
      if (err) return err;
    }
    return null;
  }
  return validateValue(c.field, c.to);
}

function validateValue(field, v) {
  if (DECIMAL_FIELDS.has(field) || INTEGER_FIELDS.has(field)) {
    if (isBlank(v)) return `${fieldLabel(field)} can't be blank`;
    const n = toNumber(v);
    if (n === null) return `${fieldLabel(field)} must be a number`;
    if (n < 0) return `${fieldLabel(field)} can't be negative`;
    if (INTEGER_FIELDS.has(field) && !Number.isInteger(n)) return `${fieldLabel(field)} must be a whole number`;
    if (field === "percentage" && n > 100) return "percentage can't exceed 100";
  }
  return null;
}

/** The POST body's `changes`, in draft order, without the client-only `key`. */
export function buildChangesPayload(draft) {
  return (Array.isArray(draft) ? draft : []).map((c) => {
    const out = { objectType: c.objectType, op: c.op, ccId: c.ccId, from: c.from, to: c.to };
    if (c.billerId !== null && c.billerId !== undefined) out.billerId = c.billerId;
    if (c.field) out.field = c.field;
    if (c.reason) out.reason = c.reason;
    return out;
  });
}

// ---------------------------------------------------------------------------
// Side-effect notes
// ---------------------------------------------------------------------------

/**
 * The note shown next to a change, or null:
 *   - enabled → 0 on a MID row: CC zeroes that MID's share;
 *   - enabled 0 → 1 with no percentage set: CC does NOT restore the old share.
 */
export function changeNote(c, draft) {
  if (!c || c.op !== "gateway.update" || c.field !== "enabled") return null;
  const off = toFlag(c.to) === false;
  const wasOn = toFlag(c.from) !== false;
  if (off && wasOn) return "Disabling sets this MID's share to 0";
  if (!off && toFlag(c.from) === false) {
    const pct = findFieldChange(draft, {
      objectType: c.objectType, ccId: c.ccId, billerId: c.billerId, field: "percentage",
    });
    if (!pct) return "Re-enabling does not restore this MID's previous share — set a percentage too";
  }
  return null;
}

// ---------------------------------------------------------------------------
// Resulting state + sum-to-100
// ---------------------------------------------------------------------------

/**
 * The object as it will be after `changes`, with gateway rows marked
 * `_draft: "added" | "removed" | "changed"`. Removed rows stay in the list (so
 * the editor can show them struck through) but are excluded from totals. A
 * disabled row's percentage is forced to 0, as CC does.
 */
export function applyChangesToItem(objectType, item, changes) {
  if (!item) return null;
  const mine = (Array.isArray(changes) ? changes : []).filter(
    (c) => c.objectType === objectType && String(c.ccId) === itemId(objectType, item)
  );
  const result = { ...item };
  for (const c of mine) {
    if (c.op === "update" && c.field) result[c.field] = c.to;
  }
  if (Array.isArray(item.gateways) || mine.some((c) => c.op === "gateway.add")) {
    let rows = (Array.isArray(item.gateways) ? item.gateways : []).map((g) => ({ ...g }));
    for (const c of mine) {
      const idx = rows.findIndex((g) => String(g.billerId).trim() === String(c.billerId));
      if (c.op === "gateway.update" && idx >= 0) {
        rows[idx] = { ...rows[idx], [c.field]: c.to, _draft: rows[idx]._draft || "changed" };
        if (c.field === "enabled" && toFlag(c.to) === false) rows[idx].percentage = 0;
      } else if (c.op === "gateway.remove" && idx >= 0) {
        rows[idx] = { ...rows[idx], _draft: "removed" };
      } else if (c.op === "gateway.add" && idx < 0) {
        rows = [...rows, { billerId: String(c.billerId), ...(c.to || {}), _draft: "added" }];
      }
    }
    result.gateways = rows;
  }
  return result;
}

function isWeighted(algorithm) {
  return String(algorithm || "").trim().toUpperCase() === "WEIGHTED";
}

/**
 * Enabled-MID percentage total for an object's resulting state. `flagged` is the
 * sum-to-100 flag: WEIGHTED and |total − 100| > 0.01 (rule 5's tolerance).
 * Removed rows don't count; a disabled row counts 0.
 */
export function resultingSummary(objectType, item, changes) {
  const r = applyChangesToItem(objectType, item, changes);
  const rows = (r?.gateways || []).filter((g) => g._draft !== "removed");
  let total = 0;
  for (const g of rows) {
    if (isGatewayEnabled(g)) total += toNumber(g.percentage) || 0;
  }
  total = Math.round(total * 100) / 100;
  const weighted = isWeighted(r?.algorithm);
  return {
    total,
    weighted,
    algorithm: r?.algorithm ?? null,
    // The epsilon keeps 99.99 (off by exactly the tolerance) inside it.
    flagged: weighted && Math.abs(total - 100) > SUM_TOLERANCE + 1e-9,
  };
}

/**
 * Every WEIGHTED profile/cascade touched by `changes` whose resulting enabled
 * total is not 100. Proposal-time these are warnings; at GO (over the TICKED
 * changes) any one of them refuses the whole set.
 */
export function sumTo100Problems(snapshot, changes) {
  const list = Array.isArray(changes) ? changes : [];
  const seen = new Set();
  const out = [];
  for (const c of list) {
    if (c.objectType !== "profiles" && c.objectType !== "cascades") continue;
    const k = `${c.objectType}|${c.ccId}`;
    if (seen.has(k)) continue;
    seen.add(k);
    const item = findItem(snapshot, c.objectType, c.ccId);
    if (!item) continue;
    const s = resultingSummary(c.objectType, item, list);
    if (s.flagged) {
      out.push({
        objectType: c.objectType,
        ccId: String(c.ccId),
        title: objectTitle(c.objectType, item, c.ccId),
        total: s.total,
      });
    }
  }
  return out;
}

export function sumProblemText(p) {
  return `${p.title} would total ${formatPercent(p.total)} across its enabled MIDs (WEIGHTED must sum to 100%)`;
}

// ---------------------------------------------------------------------------
// Labels + value formatting
// ---------------------------------------------------------------------------

function midLabelFor(item, billerId, snapshot) {
  const row = findGatewayRow(item, billerId);
  let title = row?.title;
  if (!title) {
    const gw = findItem(snapshot, "gateways", billerId);
    title = gw?.title;
  }
  const mid = resolveMid(item, billerId, title);
  return mid ? mid.label : `Gateway #${billerId}`;
}

/**
 * "Test Profile · PayArc (Seamless) · percentage". The API builds `label`
 * server-side; this is the draft tray's version and the review fallback.
 */
export function changeLabel(c, snapshot) {
  if (!c) return "";
  if (c.label) return c.label;
  const item = findItem(snapshot, c.objectType, c.ccId);
  const obj = objectTitle(c.objectType, item, c.ccId);
  switch (c.op) {
    case "update":
      return `${obj} · ${fieldLabel(c.field)}`;
    case "gateway.update":
      return `${obj} · ${midLabelFor(item, c.billerId, snapshot)} · ${fieldLabel(c.field)}`;
    case "gateway.add":
      return `${obj} · add ${midLabelFor(item, c.billerId, snapshot)}`;
    case "gateway.remove":
      return `${obj} · remove ${midLabelFor(item, c.billerId, snapshot)}`;
    case "create":
      return `New ${objectTypeLabel(c.objectType).toLowerCase()}`;
    default:
      return `${obj} · ${c.op}`;
  }
}

/** One value, formatted the way the read views format that field. */
export function formatFieldValue(field, v) {
  if (field === "percentage") return formatPercent(v);
  if (field === "enabled") return formatFlag(v);
  return displayValue(v);
}

function formatRowObject(obj) {
  if (!obj || typeof obj !== "object") return EMPTY_DISPLAY;
  const parts = [];
  if (obj.percentage !== undefined) parts.push(formatPercent(obj.percentage));
  if (obj.priority !== undefined && !isBlank(obj.priority)) parts.push(`priority ${obj.priority}`);
  if (obj.enabled !== undefined) parts.push(toFlag(obj.enabled) === false ? "disabled" : "enabled");
  if (obj.limitType !== undefined && !isBlank(obj.limitType)) parts.push(String(obj.limitType));
  if (obj.salesLimit !== undefined && toNumber(obj.salesLimit)) parts.push(`sales limit ${obj.salesLimit}`);
  if (obj.profileCap !== undefined && toNumber(obj.profileCap)) parts.push(`cap ${obj.profileCap}`);
  return parts.length ? parts.join(" · ") : "defaults";
}

/** `{ from, to }` display strings for a change row. */
export function formatChangeFromTo(c) {
  if (!c) return { from: EMPTY_DISPLAY, to: EMPTY_DISPLAY };
  if (c.op === "gateway.add") return { from: "Not on it", to: `Added · ${formatRowObject(c.to)}` };
  if (c.op === "gateway.remove") return { from: formatRowObject(c.from), to: "Removed" };
  if (c.op === "create") return { from: EMPTY_DISPLAY, to: formatRowObject(c.to) };
  return { from: formatFieldValue(c.field, c.from), to: formatFieldValue(c.field, c.to) };
}

// ---------------------------------------------------------------------------
// Re-basing a draft onto a newer snapshot (409 SNAPSHOT_OUTDATED)
// ---------------------------------------------------------------------------

/**
 * Carry a draft onto `snapshot`. A change survives only when what it assumed
 * about CC still holds there: for a field edit the live value still equals its
 * `from`; for an add the MID is still absent; for a remove it is still present
 * (its `from` is refreshed from the new row). Everything else is a conflict —
 * someone changed it outside Sound — and is dropped and reported.
 */
export function rebaseDraft(draft, snapshot) {
  const kept = [];
  const conflicts = [];
  for (const c of Array.isArray(draft) ? draft : []) {
    const item = findItem(snapshot, c.objectType, c.ccId);
    if (!item) {
      conflicts.push({ change: c, reason: "no longer exists in CheckoutChamp" });
      continue;
    }
    if (c.op === "update") {
      if (sameValue(item[c.field], c.from, c.field)) kept.push(c);
      else conflicts.push({ change: c, reason: `changed in CheckoutChamp to ${formatFieldValue(c.field, item[c.field])}` });
    } else if (c.op === "gateway.update") {
      const row = findGatewayRow(item, c.billerId);
      if (!row) conflicts.push({ change: c, reason: "that MID is no longer on it" });
      else if (sameValue(row[c.field], c.from, c.field)) kept.push(c);
      else conflicts.push({ change: c, reason: `changed in CheckoutChamp to ${formatFieldValue(c.field, row[c.field])}` });
    } else if (c.op === "gateway.add") {
      if (findGatewayRow(item, c.billerId)) conflicts.push({ change: c, reason: "that MID is already on it" });
      else kept.push(c);
    } else if (c.op === "gateway.remove") {
      const row = findGatewayRow(item, c.billerId);
      if (!row) conflicts.push({ change: c, reason: "that MID is no longer on it" });
      else kept.push(removeGatewayEdit([], { objectType: c.objectType, ccId: c.ccId, billerId: c.billerId, row })[0]);
    } else {
      kept.push(c);
    }
  }
  return { draft: kept, conflicts };
}

// ---------------------------------------------------------------------------
// Submit errors
// ---------------------------------------------------------------------------

/**
 * Classify a POST change-sets failure:
 *   outdated → 409 SNAPSHOT_OUTDATED (the only 409 that route returns)
 *   invalid  → 422, with whatever per-change list the API sent
 *   error    → anything else
 */
export function submitError(err) {
  const status = err?.response?.status;
  const body = err?.response?.data || {};
  const message = body.error || body.message || err?.message || "Submitting the proposal failed.";
  if (status === 409) {
    return {
      kind: "outdated",
      message: "CheckoutChamp routing has a newer snapshot than the one this draft was built on.",
      items: [],
    };
  }
  if (status === 422) {
    const raw = [body.changes, body.details, body.errors, body.invalid].find(Array.isArray) || [];
    return {
      kind: "invalid",
      message,
      items: raw.map((x) =>
        typeof x === "string"
          ? x
          : [x.label, x.reason || x.message || x.error].filter(Boolean).join(": ") ||
            (x.index !== undefined ? `Change ${Number(x.index) + 1} is invalid` : "A change is invalid")
      ),
    };
  }
  return { kind: "error", message, items: [] };
}

/** Warnings from the API (`warnings: [...]`) as text, whatever their shape. */
export function warningText(w) {
  if (w === null || w === undefined) return "";
  if (typeof w === "string") return w;
  if (w.message) return String(w.message);
  if (w.total !== undefined && (w.title || w.ccId)) {
    return sumProblemText({ title: w.title || `${objectTypeLabel(w.objectType)} #${w.ccId}`, total: w.total });
  }
  return displayValue(w);
}

// ---------------------------------------------------------------------------
// Change sets: status chips, counts, actions
// ---------------------------------------------------------------------------

const CHANGE_STATUS = {
  pending: { label: "Pending", badge: "badge-muted" },
  declined: { label: "Declined", badge: "badge-muted" },
  applied: { label: "Applied", badge: "badge-success" },
  failed: { label: "Failed", badge: "badge-destructive" },
  stale: { label: "Stale", badge: "badge-warning" },
  skipped: { label: "Skipped", badge: "badge-muted" },
  refused: { label: "Refused", badge: "badge-warning" },
  unverified: { label: "Unverified", badge: "badge-warning" },
};

const CHANGE_STATUS_HELP = {
  stale: "CheckoutChamp no longer had the expected value at GO, so this was not applied",
  skipped: "Not attempted — an earlier change failed and apply stops at the first failure",
  unverified: "CheckoutChamp accepted the write, but re-reading it did not show the expected value",
  refused: "Refused at GO before anything was written",
  declined: "Unticked at GO, or the set was declined",
};

export function changeStatusChip(status) {
  return CHANGE_STATUS[status] || { label: status ? String(status) : "Unknown", badge: "badge-muted" };
}

export function changeStatusHelp(status) {
  return CHANGE_STATUS_HELP[status] || null;
}

const SET_STATUS = {
  pending_approval: { label: "Pending approval", badge: "badge-info" },
  applying: { label: "Applying", badge: "badge-info" },
  applied: { label: "Applied", badge: "badge-success" },
  partially_applied: { label: "Partially applied", badge: "badge-warning" },
  failed: { label: "Failed", badge: "badge-destructive" },
  declined: { label: "Declined", badge: "badge-muted" },
  expired: { label: "Expired", badge: "badge-muted" },
};

/** Rule 12 — the API computes expiry on read; the UI double-checks `expiresAt`. */
export function effectiveSetStatus(cs, now = Date.now()) {
  if (!cs) return null;
  if (cs.status === "pending_approval" && cs.expiresAt) {
    const t = new Date(cs.expiresAt).getTime();
    if (Number.isFinite(t) && t <= now) return "expired";
  }
  return cs.status;
}

export function setStatusChip(status) {
  return SET_STATUS[status] || { label: status ? String(status) : "Unknown", badge: "badge-muted" };
}

/** `{ applied: 3, failed: 1, … }` over a set's changes. */
export function changeCounts(cs) {
  const out = {};
  for (const c of Array.isArray(cs?.changes) ? cs.changes : []) {
    const s = c?.status || "pending";
    out[s] = (out[s] || 0) + 1;
  }
  return out;
}

/** "3 applied · 1 failed" in a fixed order. */
export function changeCountsSummary(cs) {
  const counts = changeCounts(cs);
  const order = ["pending", "applied", "failed", "stale", "unverified", "skipped", "refused", "declined"];
  const known = order.filter((s) => counts[s]).map((s) => `${counts[s]} ${changeStatusChip(s).label.toLowerCase()}`);
  const other = Object.keys(counts).filter((s) => !order.includes(s)).map((s) => `${counts[s]} ${s}`);
  return [...known, ...other].join(" · ");
}

/**
 * Why a change is locked (cannot be applied on this environment), from
 * `review.perChange[changeId]`, or null. Both reasons can apply at once.
 */
export function lockReasons(perChangeEntry) {
  if (!perChangeEntry) return [];
  const out = [];
  if (perChangeEntry.verifiedOp === false) out.push("Not yet verified against CheckoutChamp");
  if (perChangeEntry.allowlisted === false) out.push("Not on this environment's write allowlist");
  return out;
}

/** The ids that start ticked: every pending change that is not locked. */
export function defaultTicked(cs, review) {
  return (Array.isArray(cs?.changes) ? cs.changes : [])
    .filter((c) => (c.status || "pending") === "pending")
    .filter((c) => lockReasons(review?.perChange?.[c.changeId]).length === 0)
    .map((c) => c.changeId);
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * The GO button, from the review payload. Never enabled when the API's review
 * says the set can't apply (`review.canApply !== true`), whatever else holds.
 *
 * @returns {{ show: boolean, enabled: boolean, label: string, reason: string|null, waiting: string|null }}
 *   show=false + waiting → a non-admin looking at a set awaiting approval.
 */
export function goButtonState({ changeSet, review, tickedIds, isAdmin, sumProblems = [], now = Date.now() }) {
  const status = effectiveSetStatus(changeSet, now);
  const ticked = (Array.isArray(tickedIds) ? tickedIds : []).filter((id) =>
    (changeSet?.changes || []).some((c) => c.changeId === id)
  );
  const label = `GO — apply ${plural(ticked.length, "change", "changes")}`;
  const off = (reason) => ({ show: true, enabled: false, label, reason, waiting: null });

  if (status !== "pending_approval" && status !== "expired") {
    return { show: false, enabled: false, label, reason: null, waiting: null };
  }
  if (!isAdmin) {
    return {
      show: false,
      enabled: false,
      label,
      reason: null,
      waiting: status === "expired" ? null : "Waiting for an internal admin to review and apply",
    };
  }
  if (status === "expired") return off("This change set expired — change sets must be applied within 7 days");
  if (!review) return off("Loading the API's review…");
  if (ticked.length === 0) return off("Tick at least one change");
  if (ticked.length > MAX_CHANGES_PER_GO) {
    return off(`At most ${MAX_CHANGES_PER_GO} changes per GO — untick ${ticked.length - MAX_CHANGES_PER_GO}`);
  }
  const lockedTicked = ticked.filter((id) => lockReasons(review.perChange?.[id]).length > 0);
  if (lockedTicked.length) {
    return off(`${plural(lockedTicked.length, "ticked change", "ticked changes")} can't be applied on this environment — untick ${lockedTicked.length === 1 ? "it" : "them"}`);
  }
  if (sumProblems.length) return off(sumProblemText(sumProblems[0]));
  if (review.canApply !== true) {
    const w = (review.warnings || []).map(warningText).filter(Boolean)[0];
    return off(w ? `The API says this set can't be applied: ${w}` : "The API says this set can't be applied");
  }
  return { show: true, enabled: true, label, reason: null, waiting: null };
}

export function canRollback(cs, isAdmin) {
  if (!isAdmin || !cs) return false;
  if (cs.status !== "applied" && cs.status !== "partially_applied") return false;
  return (cs.changes || []).some((c) => c.status === "applied");
}

export function canDecline(cs, isAdmin, now = Date.now()) {
  return !!isAdmin && effectiveSetStatus(cs, now) === "pending_approval";
}

/** 409 `{ error, refusals: [{ changeId, reason }] }` from GO → a lookup + message. */
export function applyRefusal(err) {
  const body = err?.response?.data || {};
  const byChange = {};
  for (const r of Array.isArray(body.refusals) ? body.refusals : []) {
    if (r && r.changeId) byChange[r.changeId] = r.reason || "Refused";
  }
  const general = (Array.isArray(body.refusals) ? body.refusals : [])
    .filter((r) => r && !r.changeId)
    .map((r) => r.reason)
    .filter(Boolean);
  return {
    status: err?.response?.status ?? null,
    message: body.error || err?.message || "GO was refused.",
    byChange,
    general,
  };
}
